import { type FC, memo, useCallback, useMemo, useRef } from 'react';
import { type ApiContainerAction, type ApiOrderPosition } from '@Model';
import { FlexColumn, LinkedCell, useOnTheFlyModal } from '@synqo/frontend-component-library';
import { FrameEditService } from '@Modules/Stammdaten';
import { materialsInPlaceOfPositions } from '@utils/containerMaterials';

export interface LinkedPositionsCellProps {
    positions: readonly ApiOrderPosition[];
    containerActions: readonly ApiContainerAction[];
}

interface ServiceEntry {
    key: string;
    label: string;
    serviceId: string | undefined;
}

interface LinkedServiceItemProps {
    entry: ServiceEntry;
    onOpenServiceModal: (serviceId: string | undefined) => void;
}

const LinkedServiceItem: FC<LinkedServiceItemProps> = memo(function LinkedServiceItem({
    entry,
    onOpenServiceModal,
}: LinkedServiceItemProps) {
    const handleClick = useCallback(() => {
        onOpenServiceModal(entry.serviceId);
    }, [onOpenServiceModal, entry.serviceId]);

    return <LinkedCell label={entry.label} itemId={entry.key} onClick={handleClick} />;
});

/** The services of an order: its positions, or the materials of its container actions while it has none. */
export const LinkedPositionsCell: FC<LinkedPositionsCellProps> = memo(function LinkedPositionsCell({
    positions,
    containerActions,
}: LinkedPositionsCellProps) {
    const modalIdRef = useRef('');
    const { openOnTheFlyModal, closeOnTheFlyModals } = useOnTheFlyModal();

    const closeModal = useCallback(() => {
        if (modalIdRef.current) {
            closeOnTheFlyModals(modalIdRef.current);
            modalIdRef.current = '';
        }
    }, [closeOnTheFlyModals]);

    const handleOpenServiceModal = useCallback(
        (serviceId: string | undefined) => {
            modalIdRef.current = openOnTheFlyModal({
                child: (
                    <FrameEditService
                        primaryKey={serviceId}
                        canEdit={false}
                        canCreate={false}
                        canDelete={false}
                        events={{
                            onClose: closeModal,
                        }}
                    />
                ),
            });
        },
        [openOnTheFlyModal, closeModal],
    );

    const entries = useMemo<ServiceEntry[]>(
        () => [
            ...positions.map((position) => ({
                key: position.id,
                label: position.label,
                serviceId: position.service.foreign_key,
            })),
            ...materialsInPlaceOfPositions(positions, containerActions).map((material) => ({
                key: material.foreign_key ?? material.display_string,
                label: material.display_string,
                serviceId: material.foreign_key,
            })),
        ],
        [positions, containerActions],
    );

    return (
        <FlexColumn gap="xxs">
            {entries.map((entry) => (
                <LinkedServiceItem key={entry.key} entry={entry} onOpenServiceModal={handleOpenServiceModal} />
            ))}
        </FlexColumn>
    );
});
